import { ExportIcon, PointingArrowIcon } from "@dataone/icons";
import {
  exportSearchResponseExcel,
  isEmpty,
  scrollBarStyles,
} from "@dataone/lib";
import { useMutation } from "@tanstack/react-query";
import { Divider, Skeleton, Table, Typography } from "antd";
import { MutableRefObject, useEffect } from "react";
import { styled } from "styled-components";

interface SearchDatatableProps {
  stepRef1: MutableRefObject<null>;
  data?: any[];
  handleSendTableToDashboard: () => void;
  settingId: string;
  showTextDetails: boolean;
}

const SearchDatatable = ({
  stepRef1,
  data,
  handleSendTableToDashboard,
  settingId,
  showTextDetails,
}: SearchDatatableProps) => {
  const { mutate: exportExcel, isLoading: isExporting } = useMutation(
    exportSearchResponseExcel
  );
  const datatableColumns = isEmpty(data)
    ? []
    : Object.keys(data?.[0]).map((key) => ({
        title: key,
        dataIndex: key,
        key: key,
        ellipsis: true,
        width: 50,
        render: (text: any) => {
          const isComplexValue =
            (typeof text === "object" || Array.isArray(text)) && text !== null;
          // return isComplexValue ? "Object" : text;
          return (
            <span>
              {isComplexValue ? (
                <StyledSpan>Object</StyledSpan>
              ) : (
                <Typography.Paragraph
                  editable={false}
                  style={{
                    maxWidth: "-webkit-fill-available",
                  }}
                  ellipsis={{
                    tooltip: `${text}`,
                  }}
                >
                  {text}
                </Typography.Paragraph>
              )}
            </span>
          );
        },
      }));

  const handleExportDatatable = () => {
    exportExcel(settingId, {
      onSuccess: (data) => {
        console.log(data);
      },
    });
  };

  return (
    <StyledSearchDatatable>
      <DatatableHeader>
        <h5>The Result Breakdown </h5>
        <ButtonsContainer>
          <SendButton
            onClick={handleExportDatatable}
            isExportLoading={isExporting}
            className="export"
          >
            <ExportIcon />
            <h5>{isExporting ? "Exporting ..." : "Export"}</h5>
          </SendButton>
          {/* <Divider type="vertical" /> */}
          <SendButton
            ref={stepRef1}
            onClick={() => {
              handleSendTableToDashboard();
            }}
            className="send"
          >
            <PointingArrowIcon />
            <h5>Send to Dashboard</h5>
          </SendButton>
        </ButtonsContainer>
      </DatatableHeader>
      <DatatableContent $showTextDetails={showTextDetails}>
        {data ? (
          <Table
            className="custom-table table-rows"
            size="small"
            pagination={{ defaultPageSize: 5 }}
            dataSource={data || []}
            columns={datatableColumns}
          />
        ) : (
          <div className="search-datatable-loading">
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
            <Skeleton.Input active block size="small" />
          </div>
        )}
      </DatatableContent>
    </StyledSearchDatatable>
  );
};

export default SearchDatatable;

const DatatableContent = styled.div<{ $showTextDetails: boolean }>`
  // height: calc(100vh - 440px);
  // min-height: 200px;
  .custom-table {
    // padding: 10px;
    min-height: fit-content;
    overflow: auto;
    border: 3px solid #eff1fd;
    border-radius: 12px;
    // padding: 10px;
    // max-height: 200px;
    ${scrollBarStyles};
  }

  .ant-table-cell::before {
    display: none;
  }
  .editable-cell-value-wrap {
    padding-left: 10px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    &:hover {
      cursor: text;
      padding-left: 9px;
      border: 1px solid #4974f6;
      padding-block: 4px;
      border-radius: 5px;
    }
  }
  .ant-pagination .ant-pagination-item-active {
    background: #ffffff;
  }
  .ant-table-thead .ant-table-cell {
    background-color: #4974f6;
    color: #ffffff;
  }
  .table-rows tr:nth-child(2n) td {
    background-color: #eff1fd;
    border-right: none;
    color: #000000;
    .ant-typography {
      color: #000000;
    }
  }

  .table-rows tr:nth-child(2n + 1) td {
    background-color: #ffffff;
    border-right: none;
    color: #000000;
    .ant-typography {
      color: #000000;
    }
  }
  .ant-table-wrapper
    .ant-table-tbody
    .ant-table-row:nth-child(2n)
    > .ant-table-cell-row-hover {
    background-color: #eff1fd;
    color: #000000;
    .ant-typography {
      color: #000000;
    }
  }
  .ant-table-wrapper
    .ant-table-tbody
    .ant-table-row:nth-child(2n + 1)
    > .ant-table-cell-row-hover {
    background-color: #ffffff;
    color: #000000;
    .ant-typography {
      color: #000000;
    }
  }
  .ant-table-wrapper .ant-table-thead > tr > th {
    border-bottom: none;
    border-top: none;
    border-right: none;
    height: 40px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 145px;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td {
    border-bottom: none;
    min-width: 145px;
    max-width: 145px;
    height: 45px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .ant-table-wrapper .ant-table-thead > tr > th::before {
    background-color: #000000 !important;
    display: none;
  }

  .ant-table-wrapper
    .ant-table.ant-table-bordered
    > .ant-table-container
    > .ant-table-content
    > table
    > thead
    > tr
    > th {
    border-top: 1px solid #000000;
    border-inline-end: 1px solid #000000;
  }
  .ant-table-wrapper
    .ant-table.ant-table-bordered
    > .ant-table-container
    > .ant-table-content
    > table
    > thead
    > tr
    > th:first-child {
    border-inline: 1px solid #000000;
  }
  .ant-table-wrapper
    .ant-table.ant-table-bordered
    > .ant-table-container
    > .ant-table-content
    > table
    > tbody
    > tr
    > td {
    border-inline-end: 1px solid #000000 !important;
  }
  .ant-table-wrapper
    .ant-table.ant-table-bordered
    > .ant-table-container
    > .ant-table-content
    > table
    > tbody
    > tr
    > td:first-child {
    border-inline: 1px solid #000000;
  }
  .ant-table-wrapper
    .ant-table-container
    table
    > thead
    > tr:first-child
    > *:first-child {
    border-inline-start: none;
    border-radius: 0;
  }
  .ant-table-wrapper
    .ant-table-container
    table
    > thead
    > tr:first-child
    > *:last-child {
    border-radius: 0;
  }
  .ant-table-wrapper .ant-table-container table > tbody > tr > *:first-child {
    border-inline-start: none;
  }
  .ant-typography {
    margin-bottom: 0px;
  }
`;
const StyledSearchDatatable = styled.div`
  margin-top: 20px;
  h5 {
    // color: #040726;
    font-size: 13px;
    font-weight: 700;
    padding: 10px;
    margin-bottom: 0px;
  }
  .search-datatable-loading {
    display: grid;
    grid-gap: 25px 50px;
    grid-template-columns: 2fr 5fr 3fr;
  }
`;
const DatatableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 10px 0px;
  h5 {
    margin: 0;
  }
`;
const SendButton = styled.div<{ isExportLoading?: boolean }>`
  display: flex;
  align-items: center;
  h5 {
    color: #4974f6;
    margin: 0;
    // padding-left: 0px;
    padding: 0px !important;
  }
  img {
    &:hover {
      opacity: 0.7;
    }
  }
  cursor: ${({ isExportLoading }) =>
    isExportLoading ? "not-allowed" : "pointer"};
  svg {
    display: flex;
    align-items: center;
    font-size: 20px;
    color: #4974f6;
    width: 19px;
    margin-right: 6px;
    // margin: 0px 3px 0px 3px;
  }
`;
const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  .ant-divider-vertical {
    border-inline-start: 2px solid #000 !important;
    margin: 0px 20px;
  }
  .export {
    background: #eff1fd;
    padding: 6px 10px;
    border-radius: 6px;
  }
  .send {
    padding: 4px 10px;
    border: 1.5px solid #4974f6;
    border-radius: 6px;
    margin-left: 18px;
  }
`;

const StyledSpan = styled.span`
  font-weight: 700;
  color: #4974f6;
`;
